import { post } from 'app/shared/utils/fetchr';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { isEmpty } from 'lodash';

// Selectors
import { selectHasReadEntitiesPermissions } from 'app/modules/session/selectors';

// Models
import {
  EntitiesRiskScoreResponse,
  ValidEntityRiskScore,
} from 'app/modules/riskRatings/responses';

// Constants
import { RISK_QUERY_KEYS } from 'app/modules/riskRatings/queries/keys';
import { useFetchActiveRiskModel } from 'app/modules/riskRatings/queries/useFetchActiveRiskModel';
import DataLoader from 'dataloader';

interface FetchEntityLatestRiskScorePayload {
  entity_external_ids: string[];
}

const fetchEntityRiskScore = (
  payload: FetchEntityLatestRiskScorePayload,
): Promise<EntitiesRiskScoreResponse> =>
  post(`/v1/risk/entities/scores`, payload);

const dataLoader = new DataLoader<string, EntitiesRiskScoreResponse>(
  async (ids) => {
    const response = await fetchEntityRiskScore({
      // spread to get around readonly ts issue
      entity_external_ids: [...ids],
    });
    return ids.map((id) => {
      if (response.results === undefined) {
        return response;
      }
      // shape response just like if it was a single entity API call
      return {
        results: { [id]: response.results[id] },
        risk_model: response.risk_model,
      };
    });
  },
  {
    maxBatchSize: 100,
    // batch all requests happening within 100ms
    batchScheduleFn: (callback) => setTimeout(callback, 100),
    cache: false,
  },
);

// Scores don't change often, store them for 60min in the cache
const DEFAULT_STALE_TIME = 60 * 60 * 1000;

export const useFetchEntityRiskScore = (entityExternalId: string) => {
  const { data: activeRiskModel } = useFetchActiveRiskModel();

  const hasReadEntitiesPermission = useSelector(
    selectHasReadEntitiesPermissions,
  );

  return useQuery({
    queryKey: RISK_QUERY_KEYS.getEntityRiskScore(entityExternalId),
    queryFn: () => dataLoader.load(entityExternalId),
    enabled:
      hasReadEntitiesPermission &&
      entityExternalId.length > 0 &&
      !isEmpty(activeRiskModel),
    select: (response): undefined | ValidEntityRiskScore => {
      if (response.results) {
        const { results, risk_model: riskModel } = response;
        return {
          riskModel,
          result: results[entityExternalId],
        };
      }
      return undefined;
    },
    staleTime: DEFAULT_STALE_TIME,
  });
};
